import React, { useEffect, useCallback, useState } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity, StyleSheet, Modal, Alert } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { fetchAuthSession } from 'aws-amplify/auth';
import { RootStackParamList } from './App.web'; // Assuming your stack navigator is defined somewhere as RootStackParamList
import { RouteProp } from '@react-navigation/native';
import { apiUrl } from './utils/config';
import { useStory } from './StoryContext';
import * as Font from 'expo-font';

type CharacterDetailsRouteProp = RouteProp<RootStackParamList, 'CharacterDetailsPage'>;

type CharacterDetailsNavigationProp = StackNavigationProp<
  RootStackParamList,
  'CharacterDetailsPage'
>;

const CharacterDetailsPage: React.FC<{
  route: CharacterDetailsRouteProp;
  navigation: CharacterDetailsNavigationProp;
}> = ({ route, navigation }) => {
  const { character, selectedStory, voice, sessionId, withImages } = route.params;
  const [modalVisible, setModalVisible] = useState(false);
  const areParamsValid = () => {
    return (
      character &&
      selectedStory &&
      selectedStory.first_prompt
    );
  };
  const loadFonts = async () => {
    await Font.loadAsync({
      'Futura': require('./assets/fonts/futura.ttf'),
    });
  };

  // Redirect on refresh if parameters are invalid
  useEffect(() => {
    if (!areParamsValid()) {
      console.warn('Invalid parameters detected. Redirecting to ExistingCharactersPage.');
      navigation.replace('ExistingCharactersPage', {
        selectedStory: selectedStory || {
          story_id: 'fallback-id',
          title: 'Fallback Title',
          description: 'Fallback Description',
          image_url: '',
          chapters: 0,
          prompt_number: 0,
          category: 'Fallback Category',
          status: 'Fallback Status',
          image_key: 'fallback-image-key',
          first_prompt: 'Fallback Prompt',
          images: false,
        },
        withImages: withImages || false,
        sessionId: sessionId || 'fallback-session-id',
      });
      loadFonts();
    }
  }, [character, selectedStory, sessionId, withImages, navigation]);
  const { setContext } = useStory();

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const handleDeleteCharacter = async () => {
    setModalVisible(false); // Close the modal before proceeding
    const authToken = await getAuthToken();
    if (!authToken) {
      Alert.alert('Error', 'Unable to retrieve auth token');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}characters/delete-character`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          job_id: character.job_id
        })
      });

      if (response.ok) {
        navigation.goBack();
      } else {
        const errorData = await response.json();
        Alert.alert('Error', `Failed to delete character: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error deleting character:', error);
      Alert.alert('Error', 'Failed to delete character');
    }
  };


  const handleSelectCharacter = async () => {
    const characterImageKey = character.image_url?.split('?')[0].split('/').pop();
    const authToken = await getAuthToken();
    if (!authToken) {
      Alert.alert('Error', 'Unable to retrieve auth token');
      return;
    }
  
    try {
      const response = await fetch(`${apiUrl}story/model-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        
        body: JSON.stringify({
          is_first_message: true,
          session_id: sessionId,
          character_description: character.description,
          first_prompt: selectedStory?.first_prompt,
          name: character.name,
          talent: character.talent,
          voice: voice,
          prompt_number: 1,
          images: withImages, // Include the `withImages` value
          accessories: character.accessories,
          gender: character.gender,
          story_id: selectedStory?.story_id,
          character_image_key: characterImageKey,
        }),
      });
  
      if (response.ok) {
        const data = await response.json();
        setContext({
          sessionId: data.session_id,
          storyData: {
            ...selectedStory,
            images: withImages, // Include the `withImages` value in storyData
          },
          characterDescription: character.description || '',
          withImages, // Pass the `withImages` value
          initialResponse: {
            response: data.response,
            audio: data.audio,
            session_id: sessionId || '',
          },
        });
        navigation.navigate('StoryPage', {
          sessionId: data.session_id,
          storyData: {
            ...selectedStory,
            images: withImages, // Include the `withImages` value in storyData
          },
          characterDescription: character.description || '',
          withImages, // Pass the `withImages` value
          initialResponse: {
            response: data.response,
            audio: data.audio,
            session_id: sessionId || '',
          },
        });
      } else {
        const errorData = await response.json();
        Alert.alert('Error', `Failed to start story: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error starting story:', error);
      Alert.alert('Error', 'Failed to start story');
    }
  };
  
  if (!character) {
    return <View><Text>Error: No character details found.</Text></View>;
  }

  return (
    <View style={{ flex: 1, backgroundColor: '#F7F8FB' }}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setModalVisible(true)} style={styles.deleteButton}>
          <Image source={require('./assets/delete-btn.png')} style={styles.deleteIcon} />
        </TouchableOpacity>
      </View>
      <ScrollView 
        contentContainerStyle={styles.scrollContainer} 
        style={{ flex: 1 }} 
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.container}>
          <View style={styles.details}>
            <View style={styles.info}>
              <Text style={styles.name}>{character.name}</Text>
              <Image source={{ uri: character.image_url }} style={styles.image} />
              <ScrollView style={styles.descriptionContainer} nestedScrollEnabled={true}>
                <Text style={styles.description}>{character.description}</Text>
              </ScrollView>
              <Text style={styles.sectionTitle}>Talent</Text>
              <Text style={styles.talent}>{character.talent}</Text>
              <View style={styles.buttonContainer}>
                <View style={styles.selectShadowBackground} />
                <TouchableOpacity onPress={() => handleSelectCharacter()} style={styles.selectButton}>
                  <Text style={styles.buttonText}>Next</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        {/* Confirmation Modal */}
        <Modal visible={modalVisible} transparent={true} animationType="slide">
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Delete Character</Text>
              <Text style={styles.modalConfirmation}>Are you sure you want to delete this character? This action cannot be undone.</Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity
                  style={[styles.modalButton, styles.cancelButton]}
                  onPress={() => setModalVisible(false)}
                >
                  <Text style={styles.confirmButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalButton, styles.confirmDeleteButton]}
                  onPress={handleDeleteCharacter}
                >
                  <Text style={styles.confirmButtonText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: '#F7F8FB',
    alignItems: 'center',
    flexGrow: 1,
  },
  scrollContainer: {
    paddingBottom: 20, // Prevents content from being cut off at the bottom
    paddingTop: 60,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: 60, // Pick a height that matches your design
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  backButton: {
    marginLeft: 10,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  deleteButton: {
    marginRight: 10,
  },
  deleteIcon: {
    width: 24,
    height: 24,
  },
  details: {
    backgroundColor: '#F7F8FB',
    borderRadius: 15,
    padding: 20,
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: 500,
  },
  image: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 1,
    borderRadius: 15,
    marginBottom: 20,
    alignSelf: 'center',  // Keep image centered
  },
  info: {
    width: '100%',
    alignItems: 'flex-start'  // Align text to the left
  },
  name: {
    fontSize: 24,
    fontWeight: 'bold',
    fontFamily: 'Futura',
    textAlign: 'left',
    marginBottom: 10,
    width: '100%'  // Ensure full width for alignment
  },
  descriptionContainer: {
    width: '100%', // Full width of the container
    maxHeight: '30%', // Set a max height for the ScrollView
  },
  description: {
    fontSize: 16,
    textAlign: 'left',
    fontFamily: 'Futura',
    width: '100%',  // Ensure full width for alignment
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#160014',
    marginTop: 20,
    marginBottom: 10,
    textAlign: 'left',
    width: '100%',  // Ensure full width for alignment
    fontFamily: 'Futura'
  },
  talent: {
    fontSize: 16,
    color: '#160014',
    textAlign: 'left',
    marginBottom: 20,
    width: '100%',  // Ensure full width for alignment
    fontFamily: 'Futura'
  },
  buttonContainer: {
    alignSelf: 'center',
  },
  selectButton: {
    backgroundColor: '#544564', 
    padding: 15, 
    paddingHorizontal: 100,
    borderRadius: 15, 
    alignItems: 'center', 
    alignSelf: 'center',
    width: 238,
    height: 55,
    marginTop: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  selectShadowBackground: {
    position: 'absolute',
    width: 238,
    height: 55,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '25%',
    left: '1%',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    maxWidth: 400,
    width: '80%',
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 10,
    alignItems: 'center',
    fontFamily: 'Futura',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    fontFamily: 'Futura',
  },
  modalConfirmation: {
    fontFamily: 'Futura',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    marginTop: 20,
  },
  modalButton: {
    flex: 1, // Allow buttons to evenly distribute space
    paddingVertical: 10,
    marginHorizontal: 5, // Add horizontal margin
    borderRadius: 10, // Add border radius
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: 'gray',
  },
  confirmDeleteButton: {
    backgroundColor: 'red',
  },
  confirmButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
});

export default CharacterDetailsPage;
